import { Datepicker } from "@/components/datepicker/Datepicker";
import useFormatter from "@/hooks/use-formatter.hook";
import { useProbationPolicies } from "@/queries/use-probation-policies.query";
import { Rounding } from "@/types/common.types";
import { ProbationPolicyUnitTypes } from "@/types/probation-policy.types";
import {
  Button,
  FullScreenPage,
  LinkButtonTo,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from "@skailer-com/uikit";
import { addDays, addMonths, startOfDay, subDays } from "date-fns";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HireStep, useHireContext } from "../hire-context";
import { getHirePackageColumns } from "../hire-form";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook";
import { ApiTableTypes } from "@/types/custom-field.types";
import { HireTitleSlot } from "../hire-edit-pages";
import { HireGradientTop } from "./HireGradientTop";
import {
  HireAfterProbationPackage,
  HireAfterProbationPackageHandle,
} from "./HireAfterProbationPackage";
import { HireSidebar } from "./HireSidebar";
import { HireWorkflowsSection } from "./HireWorkflowsSection";

const inlineErrorKeys = ["date_probation_ends", "compensation_after_probation"];

export function HireOnboardingStep() {
  const { t } = useTranslation();
  const { renderDate, getDaysToDate } = useFormatter();
  const {
    flow,
    employee,
    onboarding: value,
    setOnboarding,
    complete,
    isSubmitting,
    isPreview,
    errors,
    steps,
    goToStep,
    exit,
  } = useHireContext();
  const previousStep = steps[steps.indexOf(HireStep.onboarding) - 1];
  const packageRef = useRef<HireAfterProbationPackageHandle>(null);
  const { fields: compensationTableFields } = useSystemTableConfig(
    ApiTableTypes.employee_compensations
  );
  const columnSlugs = new Set(
    getHirePackageColumns(compensationTableFields).map((field) => field.slug)
  );
  const { data: policies } = useProbationPolicies(flow.probation_is_enabled);
  const [isCustomDateEnd, setIsCustomDateEnd] = useState(false);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const messages = { ...errors[HireStep.onboarding], ...localErrors };
  const policy = policies?.find((item) => item.id === value.probation_policy_id);
  const hireDate = employee?.date_hired_on ? new Date(employee.date_hired_on) : new Date();
  const policyEnd = policy
    ? policy.unit === ProbationPolicyUnitTypes.months
      ? subDays(addMonths(hireDate, policy.amount), 1)
      : addDays(hireDate, policy.amount)
    : null;
  const dateEnd = flow.probation_policy_locked
    ? policyEnd
    : (value.date_probation_ends ?? policyEnd);
  const submit = async () => {
    if (isSubmitting) return;
    const next: Record<string, string> = {};
    const required = t("errors:field_required");
    if (value.enable_probation && !flow.probation_policy_locked) {
      if (!dateEnd) next.date_probation_ends = required;
      else if (startOfDay(dateEnd) < startOfDay(hireDate))
        next.date_probation_ends = t("hiring_flow:probation_date_invalid");
    }
    const isPackageValid =
      value.enable_probation &&
      value.compensation_enabled &&
      flow.compensation_after_probation_is_enabled
        ? ((await packageRef.current?.validate()) ?? true)
        : true;
    if (flow.workflows_is_mandatory && !value.workflows.length) next.workflows = required;
    setLocalErrors(next);
    if (Object.keys(next).length || !isPackageValid) return;
    await complete({ ...value, date_probation_ends: dateEnd });
  };
  const button = (
    <Button size="lg" isLoading={isSubmitting} onClick={submit}>
      {t("common:complete")}
    </Button>
  );
  return (
    <FullScreenPage
      isCenterVertical={false}
      backSlot={
        <Button size="lg" variant="secondary" onClick={exit} disabled={isSubmitting}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={<HireTitleSlot step={HireStep.onboarding} employee={employee} />}
      backgroundSlot={<HireGradientTop />}
      sidebarSlot={<HireSidebar step={HireStep.onboarding} />}
      actionsSlot={
        <Stack direction="horizontal" gap="sm">
          <Button
            size="lg"
            variant="secondary"
            disabled={isSubmitting}
            onClick={() => goToStep(previousStep)}
          >
            {t("common:back")}
          </Button>
          {button}
        </Stack>
      }
    >
      <Stack gap="sm">
        {isPreview && <Text className="text-secondary">{t("hiring_flow:preview_hint")}</Text>}
        {flow.probation_is_enabled && (
          <Stack gap="2xl" className="card-panel">
            <Title header={t("core:probation")} />
            <Stack gap="md">
              <Switch
                checked={value.enable_probation}
                disabled={isSubmitting || flow.probation_is_mandatory}
                title={t("core:enable_probation_period")}
                description={t("core:select_probation_policy")}
                onChange={(enable_probation) => setOnboarding({ ...value, enable_probation })}
              />
              {value.enable_probation && (
                <Stack>
                  <Select
                    label={t("core:select_probation_policy")}
                    emptyTitle={t("common:select")}
                    value={value.probation_policy_id}
                    options={policies?.map((item) => ({ id: item.id, title: item.title })) ?? []}
                    disabled={isSubmitting || flow.probation_policy_locked}
                    onChange={(id) => {
                      setIsCustomDateEnd(false);
                      setOnboarding({
                        ...value,
                        probation_policy_id: id == null ? null : Number(id),
                        date_probation_ends: null,
                      });
                    }}
                  />
                  {dateEnd && (
                    <Stack className="py-2 text-sm text-secondary" direction="horizontal">
                      {t("core:ends_on_period", {
                        period_end: renderDate(dateEnd),
                        days_to_end: t("plurals:plural_day", {
                          count: getDaysToDate(addDays(dateEnd, 1), Rounding.up),
                        }),
                      })}
                      {!flow.probation_policy_locked && (
                        <>
                          &nbsp;
                          <LinkButtonTo
                            onClick={() => setIsCustomDateEnd(true)}
                            className="text-sm text-secondary"
                          >
                            {t("common:change")}
                          </LinkButtonTo>
                        </>
                      )}
                    </Stack>
                  )}
                  {!flow.probation_policy_locked &&
                    (isCustomDateEnd || value.date_probation_ends || !policy) && (
                      <Datepicker
                        label={t("core:ends_on")}
                        value={dateEnd ?? undefined}
                        disabled={isSubmitting}
                        onChange={(date) =>
                          setOnboarding({ ...value, date_probation_ends: date ?? null })
                        }
                      />
                    )}
                  {messages.date_probation_ends && (
                    <Text className="text-danger text-sm">{messages.date_probation_ends}</Text>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
        {flow.probation_is_enabled &&
          value.enable_probation &&
          flow.compensation_after_probation_is_enabled && (
            <Stack className="card-panel" gap="lg">
              <Switch
                checked={value.compensation_enabled}
                disabled={isSubmitting || flow.compensation_after_probation_is_mandatory}
                title={t("hiring_flow:compensation_after_probation_title")}
                onChange={(compensation_enabled) =>
                  setOnboarding({ ...value, compensation_enabled })
                }
              />
              <HireAfterProbationPackage
                ref={packageRef}
                disabled={isSubmitting || !value.compensation_enabled}
              />
            </Stack>
          )}
        {flow.workflows_is_enabled && <HireWorkflowsSection />}
        {Object.entries(messages)
          .filter(([key]) => !inlineErrorKeys.includes(key) && !columnSlugs.has(key))
          .map(([key, message]) => (
            <Text key={key} className="text-danger">
              {message}
            </Text>
          ))}
        {button}
      </Stack>
    </FullScreenPage>
  );
}
